<template>
  <div class="interactive-slider">
    <div class="interactive-slider__slides">
      <transition-group
        @before-enter="beforeEnter"
        @enter="enter"
        @afterEnter="afterEnter"
        @beforeLeave="beforeLeave"
        @leave="leave"
        @afterLeave="afterLeave"
      >      
        <div
          v-for="(slide, index) in dataSlides"
          class="interactive-slider__slide touch"
          :class="[
            { 'interactive-slider__slide--active' : activeSlideId === slide._id }, 
            { 'interactive-slider__slide--inactive' : activeSlideId !== null && activeSlideId !== slide._id }, 
            { 'interactive-slider__slide--expand' : activeSlideId === slide._id && slide.shouldSlideExpand }
          ]"
          :data-index="index"
          :key="slide._id"
          @click="setActiveSlideId(slide._id)"
        >
            <div class="interactive-slider__label" v-html="slide.label[lang]"></div>
            <transition name="fast-fade">
              <div v-if="slide.asset.type === 'video' && activeSlideId !== slide._id" class="interactive-slider__play-icon">
                <fa :icon="['fas', 'play-circle']"/>
              </div>
            </transition>
            <video
              v-if="slide.asset.type === 'video'"
              :id="`video-${slide._id}`"
              class="interactive-slider__video"
              :src="`${$store.state.assetsPath + slide.asset.path}`"
              muted
              loop
            ></video>
            <img 
              v-if="slide.asset.type === 'video'" 
              class="interactive-slider__video-poster"
              :src="`${$store.state.assetsPath}${slide.asset.customThumbPath ? slide.asset.customThumbPath : slide.asset.thumbPath}`"
            >
            <img v-if="slide.asset.type === 'image'" :src="$store.state.assetsPath + slide.asset.path">
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
export default {
  props: ['lang', 'slides', 'activeSlideId'],

  data () {
    return {
      dataSlides: []
    }
  },

  computed: {
    activeSlide () {
      return _.find(this.slides, slide => {
        return slide._id === this.activeSlideId
      })
    },
  },

  methods: {
    setActiveSlideId (id) {
      if (this.activeSlideId === id) {
        // Slide is already active, deactivate it
        id = null
      }
      this.$emit('setActiveSlide', id)
    },

    startVideo (slideId) {
      setTimeout(() => {
        this.$el.querySelector(`#video-${slideId}`).play()
      }, 310)
    },

    stopVideos () {
      this.$el.querySelectorAll(`video:not(#video-${this.activeSlideId}`).forEach(videoEl => {
        setTimeout(() => {
          videoEl.pause()
        }, 300)
        setTimeout(() => {
          videoEl.currentTime = 0
        }, 500)
      })
    },

      beforeEnter (el) {
        el.removeAttribute('style')
        el.style.opacity = 0
        el.style.transform = 'translateY(25%)'
      },

      enter (el, done) {
        setTimeout(() => {
          el.style.opacity = 1
          el.style.transform = 'translateY(0)'
          el.style.transition = `transform 0.6s cubic-bezier(0.33, 1, 0.68, 1), opacity 1.2s`
          setTimeout(() => {
            done()
          }, 300)
        }, (parseInt(el.dataset.index) + 1) * 150)
      },

      afterEnter (el) {
        if (parseInt(el.dataset.index) + 1 === this.dataSlides.length) {
          this.$store.commit('INTERACTIVE_SLIDES_ANIMATE_END')
        }      
        el.removeAttribute('style')
      },

      beforeLeave (el) {
        el.style.transition = `transform 0.3s cubic-bezier(0.32, 0, 0.67, 0), opacity 0.3s`
      },

      leave (el) {
        el.style.opacity = 0
        el.style.transform = 'translateY(25%)'
      },

      afterLeave (el) {
        el.removeAttribute('style')
      }
  },

  watch: {
    activeSlide () {
      this.stopVideos()
      if (this.activeSlide && this.activeSlide.asset.type === 'video') {
        this.startVideo(this.activeSlideId)
      }
    },

    slides (newSlides, oldSlides) {
      if (JSON.stringify(newSlides) !== JSON.stringify(oldSlides)) {
        this.$store.commit('INTERACTIVE_SLIDES_ANIMATE_START')
        this.activeSlideId = null
        this.dataSlides = []
        setTimeout(() => {
          this.dataSlides = this.slides
        }, 300)
      }
    }
  },

  mounted () {
    // if not animating..
    this.$store.commit('INTERACTIVE_SLIDES_ANIMATE_START')
    this.dataSlides = this.slides
  }
}
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
$transition-speed: 300ms;

.interactive-slider {
  bottom: 7.5rem;
  position: absolute;
  width: 100%;
  z-index: 20;

  &__slides {
    align-items: flex-end;
    display: flex;
    justify-content: center;
  }
  
  &__slide {
    border-radius: .5rem;
    box-shadow: $tag-box-shadow;
    display: inline-block;
    flex-shrink: 0;
    height: 8.25rem;
    width: 12.5rem;
    margin: 0 .6rem;
    overflow: hidden;
    position: relative;
    transition: all $transition-speed ease-in-out;

    &--active {
      box-shadow:
        0 0 .5rem .1rem rgb(255, 255, 255);
      opacity: 1;
    }

     &--inactive {
      opacity: .4;
    }

    &--expand {
      margin: 0 1rem;
      height: calc(8.25rem * 2.5);
      width: calc(12.5rem * 2.5);
    }

    img {
      position: absolute;
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  }

  &__video {
    position: absolute;
    height: 100%;
    object-fit: cover;
    width: 100%;

    &-poster {
      transition: opacity 0.3s;

      .interactive-slider__slide--active & {
        opacity: 0;
      }
    }
  }

  &__play-icon {
    font-size: 2rem;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }

  &__label {
    align-items: center;
    background: rgba(0, 0, 0, .6);
    bottom: 0;
    display: flex;
    font-size: .6rem;
    font-weight: 300;
    justify-content: center;
    height: 2.1rem;
    letter-spacing: .05rem;
    padding: .2rem .75rem;
    position: absolute;
    text-align: center;
    text-transform: uppercase;
    width: 100%;
    z-index: 3;
  }
}
</style>